import { table, text } from '../ui';
import { projectZone } from '../schedule.mjs';

const PLURALS = { person: 'people' };
const plural = (n, word) => `${n} ${n === 1 ? word : PLURALS[word] || `${word}s`}`;

export default function RotationFrequency({ zoneNames, roster, clrRoster = [], blockLabels, blocksPerWeek, previewNote }) {
  const lastBlock = blockLabels[blockLabels.length - 1];

  // CLR is one person per week over the whole rotation, so each member of it
  // comes round every clrRoster.length weeks whichever zone they sit in.
  const clrCycle = clrRoster.length;

  const rows = zoneNames.map((tz) => {
    const people = roster?.[tz] || [];
    return {
      tz,
      inClr: people.filter((name) => clrRoster.includes(name)).length,
      ...projectZone(people.length, blocksPerWeek),
    };
  });

  const staffed = rows.filter((row) => row.size > 0);
  const freePerWeek = staffed.reduce((sum, row) => sum + row.freePerWeek, 0);
  const total = staffed.reduce((sum, row) => sum + row.size, 0);
  const freeZones = staffed.filter((row) => row.freePerWeek > 0).map((row) => row.tz);

  return (
    <details className="mt-4 bg-gray-50 rounded">
      <summary className="cursor-pointer select-none px-4 py-2 font-medium text-sm">
        Projection per zone
        {previewNote && (
          <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-normal">
            {previewNote}
          </span>
        )}
      </summary>
      <div className="px-4 pb-4">
      <p className={`${text.hint} mb-3`}>
        A zone of N people repeats every N weeks, and everyone takes each block once in that cycle.
      </p>

      <table className="w-full text-sm">
        <thead className="text-xs text-gray-500">
          <tr>
            <th className={table.head}>Zone</th>
            <th className={table.head}>People</th>
            <th className={table.head}>How often each person is SLR</th>
            <th className={table.head}>Off SLR each week, so free to take CLR</th>
            <th className={table.head}>How often each person is CLR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.size === 0) {
              return (
                <tr key={row.tz} className="border-t">
                  <td className="p-2 font-medium">{row.tz}</td>
                  <td className="p-2" colSpan={4}>
                    <span className={text.hint}>nobody assigned</span>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={row.tz} className="border-t">
                <td className="p-2 font-medium">{row.tz}</td>
                <td className="p-2">{row.size}</td>
                <td className="p-2">
                  {row.weeksOff === 0
                    ? 'every week'
                    : `${row.weeksOnDuty} weeks out of every ${row.cycleWeeks}`}
                  {row.blocksPerTurn > 1 && (
                    <span className={text.hint}> ({row.blocksPerTurn} blocks per week)</span>
                  )}
                  <div className={text.hintTight}>
                    {lastBlock} every {plural(row.cycleWeeks, 'week')}
                  </div>
                </td>
                <td className="p-2">
                  {row.freePerWeek === 0 ? (
                    <span className="text-amber-700">nobody, ever</span>
                  ) : (
                    `${plural(row.freePerWeek, 'person')} of ${row.size}`
                  )}
                </td>
                <td className="p-2">
                  {row.inClr === 0 || clrCycle === 0 ? (
                    <span className={text.hint}>not in the rotation</span>
                  ) : (
                    <>
                      every {plural(clrCycle, 'week')}
                      {row.inClr < row.size && (
                        <div className={text.hintTight}>
                          {row.inClr} of {row.size} in the rotation
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-3 p-3 bg-white border rounded text-sm">
        <p className="font-medium mb-1">Can CLR avoid clashing with an SLR shift?</p>
        {total === 0 ? (
          <p className={text.hint}>Nobody is assigned to a zone yet.</p>
        ) : freePerWeek === 0 ? (
          <p>
            <span className="text-amber-700 font-medium">No.</span> At{' '}
            {plural(blocksPerWeek, 'block')} per week every zone needs everyone every week, so
            whoever holds CLR is also on a shift. A zone frees somebody only once it has{' '}
            <strong>more than {plural(blocksPerWeek, 'person')}</strong>. The two levers are adding
            people to a zone or lowering the blocks per week.
          </p>
        ) : (
          <p>
            <span className="text-green-700 font-medium">Partly.</span> {freePerWeek} of {total}{' '}
            people are off SLR in any given week ({freeZones.join(', ')}), so CLR can go to one of
            them without a clash. The other {plural(total - freePerWeek, 'person')} are on a shift
            every week, so a clash-free CLR cannot rotate through the whole team.
          </p>
        )}
      </div>
      </div>
    </details>
  );
}
